import React from 'react';
import PropTypes from 'prop-types';
import {
  PROJECT_CARD_ASPECT_RATIO,
  PROJECT_CARD_PREVIEW_WIDTH,
} from 'helpers/projectCoverAspect';
import { removeMarkdownSyntax } from 'helpers/HelperFunctions';
import styles from './ProjectThumbnailCard.module.scss';

/**
 * In-form / modal portfolio-card preview at the live card aspect ratio.
 * Body matches portfolio cards: title, one description line, one role line.
 */
const ProjectThumbnailCard = ({
  imageUrl,
  title,
  description,
  firstRole,
  cropSource,
  loading,
  square,
  width,
  showPlayBadge,
  badgeLabel,
  frameLabel,
}) => {
  const displayTitle = (title && title.trim()) || 'Untitled project';
  const displayDescription = description
    ? removeMarkdownSyntax(description).replace(/\s+/g, ' ').trim()
    : '';
  const displayRole = (firstRole && String(firstRole).trim()) || '';

  let resolvedBadge = badgeLabel;
  if (!resolvedBadge) {
    if (showPlayBadge && cropSource === 'auto') {
      resolvedBadge = 'Automatic · mid-video frame';
    } else if (cropSource === 'custom') {
      resolvedBadge = 'Custom crop';
    } else {
      resolvedBadge = 'Automatic crop';
    }
  }

  return (
    <div
      className={styles.card}
      style={{ width }}
      data-aspect={square ? '1' : String(PROJECT_CARD_ASPECT_RATIO)}
    >
      <div
        className={`${styles.imageArea}${square ? ` ${styles.square}` : ''}`}
      >
        {loading ? (
          <div className={styles.skeleton} aria-hidden />
        ) : imageUrl ? (
          <img src={imageUrl} alt="" className={styles.image} />
        ) : (
          <div className={styles.placeholder} aria-hidden />
        )}
        {showPlayBadge && !loading && (
          <span className={styles.playBadge} aria-hidden>
            <span className={styles.playTriangle} />
          </span>
        )}
        {!loading && frameLabel ? (
          <span className={styles.frameLabel}>{frameLabel}</span>
        ) : null}
        {!loading && (
          <span className={styles.badge} aria-label={resolvedBadge}>
            {resolvedBadge}
          </span>
        )}
      </div>
      <div className={styles.body}>
        {loading ? (
          <>
            <div className={styles.skeletonTitle} aria-hidden />
            <div className={styles.skeletonDescription} aria-hidden />
            <div className={styles.skeletonRole} aria-hidden />
          </>
        ) : (
          <>
            <p className={styles.title}>{displayTitle}</p>
            {displayDescription ? (
              <p className={styles.description}>{displayDescription}</p>
            ) : null}
            {displayRole ? <p className={styles.role}>{displayRole}</p> : null}
          </>
        )}
      </div>
    </div>
  );
};

ProjectThumbnailCard.propTypes = {
  imageUrl: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  firstRole: PropTypes.string,
  cropSource: PropTypes.oneOf(['auto', 'custom']),
  loading: PropTypes.bool,
  square: PropTypes.bool,
  width: PropTypes.number,
  showPlayBadge: PropTypes.bool,
  badgeLabel: PropTypes.string,
  frameLabel: PropTypes.string,
};

ProjectThumbnailCard.defaultProps = {
  imageUrl: null,
  title: '',
  description: '',
  firstRole: '',
  cropSource: 'auto',
  loading: false,
  square: false,
  width: PROJECT_CARD_PREVIEW_WIDTH,
  showPlayBadge: false,
  badgeLabel: null,
  frameLabel: null,
};

export default ProjectThumbnailCard;
